import type { ReactNode } from 'react';

/**
 * Markdown-style link: [label](https://example.com)
 * Captures group 1 = label, group 2 = url.
 */
const LINK_PATTERN = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

/**
 * Render a plain string that may contain markdown-style links
 * (`[label](https://url)`) as React nodes, with the links rendered as safe
 * anchor tags. Only `http(s)` URLs are linkified — anything else is left as
 * literal text — so admin-authored copy can include links without exposing
 * raw HTML injection.
 *
 * Used by the entry-confirmation popup (DKPLAT-515) so tenants like SkyPicks
 * can link out to their official rules from the confirmation copy.
 */
export function renderTextWithLinks(
  text: string,
  linkClassName = 'text-pickem-primary underline hover:text-pickem-primary-light'
): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Reset lastIndex — the regex is declared with the global flag at module scope.
  LINK_PATTERN.lastIndex = 0;

  while ((match = LINK_PATTERN.exec(text)) !== null) {
    const [full, label, url] = match;
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    nodes.push(
      <a
        key={`${match.index}-${url}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName}
        onClick={e => e.stopPropagation()}
      >
        {label}
      </a>
    );
    lastIndex = match.index + full.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}
